import { HEROES, canEquipItem, type HeroId } from '../content/heroes';
import { SKILLS, TRAIT_IDS, aspectOptions, aspectPath } from '../content/skills';
import { ITEMS, type ItemInst, type ItemSlot } from '../content/items';
import { RANK_COST, RESPEC_FREE_BELOW_RANK, rankPoints } from '../content/stats';
import type { World } from './world';
import { BACKPACK_SLOTS, type HeroSave, type ItemSave, type KitSkillSave, type PlayerEnt, type SkillSlot } from './types';
import { recomputeStats } from './player';
import { spawnPickup } from './loot';

/**
 * Rank spending: the deliberate half of progression. Points come from rank, the kit is bought with them,
 * and everything here survives the realm. Nothing random lives in this file.
 * The same operations run on a living hero (in a realm) and on a resting one (from the menu).
 */
export const MAX_ACTIVES = 4;
export const MAX_AUTOS = 3;

/** Kit changes: they cost rank points and mean the same thing in a realm and in the menu. */
export type KitOp =
  | { op: 'unlock'; id: string }
  | { op: 'aspect'; id: string; aspect: string }
  | { op: 'trait'; id: string }
  | { op: 'respec' }
  | { op: 'auto'; id: string; on: boolean };

/** What a hero inside a realm may do; items are addressed by uid because the bag changes under them. */
export type BuildOp = KitOp | { op: 'equip'; uid: number } | { op: 'unequip'; slot: ItemSlot } | { op: 'drop'; uid: number };

/** What a resting hero may do from the menu; the saved bag is a plain list, so items go by index. */
export type SaveOp = KitOp | { op: 'equip'; index: number } | { op: 'unequip'; slot: ItemSlot };

export interface BuildResult {
  ok: boolean;
  /** i18n key of the refusal */
  err?: string;
}

const ok: BuildResult = { ok: true };
const no = (err: string): BuildResult => ({ ok: false, err });

export function heroPool(heroId: HeroId): string[] {
  return HEROES[heroId]?.skills ?? [];
}

/** Traits a hero may rank: those weighted for the hero, plus the generic ones without any hero weights. */
export function traitPool(heroId: HeroId): string[] {
  return TRAIT_IDS.filter((id) => {
    const def = SKILLS[id];
    if (!def) return false;
    if (!def.heroWeight) return true;
    return (def.heroWeight[heroId] ?? 0) > 0;
  });
}

// ------------------------------------------------------------------ the kit, whatever holds it

interface KitSlotLike {
  id: string;
  aspects: string[];
  auto?: boolean;
}

interface KitLike<T extends KitSlotLike> {
  heroId: HeroId;
  rank: number;
  actives: T[];
  autos: T[];
  traits: { id: string; level: number }[];
}

const playerSlot = (id: string): SkillSlot => ({ id, level: 1, aspects: [], cd: 0 });
const saveSlot = (id: string): KitSkillSave => ({ id, aspects: [] });

export function kitSpent<T extends KitSlotLike>(kit: KitLike<T>): number {
  const start = HEROES[kit.heroId]?.startSkill;
  let n = 0;
  for (const s of [...kit.actives, ...kit.autos]) {
    if (s.id !== start) n += RANK_COST.skill;
    if (s.aspects.length >= 1) n += RANK_COST.aspect1;
    if (s.aspects.length >= 2) n += RANK_COST.aspect2;
  }
  for (const t of kit.traits) n += t.level * RANK_COST.trait;
  return n;
}

export function kitFree<T extends KitSlotLike>(kit: KitLike<T>): number {
  return rankPoints(kit.rank) - kitSpent(kit);
}

/** Cost of the next aspect on this skill, or 0 when both rings are already chosen. */
export function aspectCost(aspects: string[]): number {
  return aspects.length === 0 ? RANK_COST.aspect1 : aspects.length === 1 ? RANK_COST.aspect2 : 0;
}

function applyKitOp<T extends KitSlotLike>(kit: KitLike<T>, op: KitOp, make: (id: string) => T): BuildResult {
  const hero = HEROES[kit.heroId];
  const find = (id: string) => kit.actives.find((s) => s.id === id) ?? kit.autos.find((s) => s.id === id);
  switch (op.op) {
    case 'unlock': {
      const def = SKILLS[op.id];
      if (!def || def.kind === 'trait' || !heroPool(kit.heroId).includes(op.id)) return no('build.notInPool');
      if (find(op.id)) return no('build.owned');
      if (kitFree(kit) < RANK_COST.skill) return no('build.noPoints');
      if (def.kind === 'active') {
        if (kit.actives.length >= MAX_ACTIVES) return no('build.activesFull');
        kit.actives.push(make(op.id));
      } else {
        if (kit.autos.length >= MAX_AUTOS) return no('build.autosFull');
        kit.autos.push(make(op.id));
      }
      return ok;
    }
    case 'aspect': {
      const slot = find(op.id);
      if (!slot) return no('build.notOwned');
      if (slot.aspects.length >= 2) return no('build.pathFull');
      if (!aspectOptions(op.id, slot.aspects).some((a) => a.id === op.aspect)) return no('build.badAspect');
      if (kitFree(kit) < aspectCost(slot.aspects)) return no('build.noPoints');
      slot.aspects = [...slot.aspects, op.aspect];
      return ok;
    }
    case 'trait': {
      const def = SKILLS[op.id];
      if (!def || def.kind !== 'trait' || !traitPool(kit.heroId).includes(op.id)) return no('build.notInPool');
      const t = kit.traits.find((x) => x.id === op.id);
      if (t && t.level >= def.maxLevel) return no('build.maxed');
      if (kitFree(kit) < RANK_COST.trait) return no('build.noPoints');
      if (t) t.level++;
      else kit.traits.push({ id: op.id, level: 1 });
      return ok;
    }
    case 'respec': {
      const start = kit.actives.find((s) => s.id === hero.startSkill);
      kit.actives = [start ? { ...start, aspects: [], auto: undefined } : make(hero.startSkill)];
      kit.autos = [];
      kit.traits = [];
      return ok;
    }
    case 'auto': {
      const slot = kit.actives.find((s) => s.id === op.id);
      if (!slot) return no('build.notOwned');
      slot.auto = op.on || undefined;
      return ok;
    }
  }
}

/**
 * Drops kit pieces the hero can no longer afford (content changes, migrated saves) until the
 * spent points fit the rank: ring two first, then ring one, then traits, then extra skills.
 */
function enforceKit<T extends KitSlotLike>(kit: KitLike<T>, make: (id: string) => T): void {
  const hero = HEROES[kit.heroId];
  const pool = heroPool(kit.heroId);
  kit.actives = kit.actives.filter((s) => SKILLS[s.id]?.kind === 'active' && (pool.includes(s.id) || s.id === hero.startSkill));
  kit.autos = kit.autos.filter((s) => SKILLS[s.id]?.kind === 'auto' && pool.includes(s.id));
  if (!kit.actives.some((s) => s.id === hero.startSkill)) kit.actives.unshift(make(hero.startSkill));
  kit.actives = kit.actives.slice(0, MAX_ACTIVES);
  kit.autos = kit.autos.slice(0, MAX_AUTOS);
  for (const s of [...kit.actives, ...kit.autos]) s.aspects = aspectPath(s.id, s.aspects).map((a) => a.id);
  const traits = traitPool(kit.heroId);
  kit.traits = kit.traits.filter((t) => traits.includes(t.id)).map((t) => ({ id: t.id, level: Math.max(1, Math.min(SKILLS[t.id]?.maxLevel ?? 1, t.level)) }));
  let guard = 200;
  while (kitSpent(kit) > rankPoints(kit.rank) && guard-- > 0) {
    const ring2 = [...kit.actives, ...kit.autos].find((s) => s.aspects.length === 2);
    if (ring2) {
      ring2.aspects = ring2.aspects.slice(0, 1);
      continue;
    }
    const ring1 = [...kit.actives, ...kit.autos].find((s) => s.aspects.length === 1);
    if (ring1) {
      ring1.aspects = [];
      continue;
    }
    const trait = kit.traits[kit.traits.length - 1];
    if (trait) {
      if (trait.level > 1) trait.level--;
      else kit.traits.pop();
      continue;
    }
    if (kit.autos.length) {
      kit.autos.pop();
      continue;
    }
    const extra = kit.actives.findIndex((s) => s.id !== hero.startSkill);
    if (extra >= 0) {
      kit.actives.splice(extra, 1);
      continue;
    }
    break;
  }
}

// ------------------------------------------------------------------ a hero inside a realm

export function spentPoints(p: PlayerEnt): number {
  return kitSpent(p);
}

export function freePoints(p: PlayerEnt): number {
  return kitFree(p);
}

export function respecIsFree(rank: number): boolean {
  return rank < RESPEC_FREE_BELOW_RANK;
}

export function enforceBudget(p: PlayerEnt): void {
  enforceKit(p, playerSlot);
}

/** Applies a build change; the caller has already settled any fame cost (respec at high rank). */
export function applyBuild(world: World, p: PlayerEnt, op: BuildOp): BuildResult {
  let r: BuildResult;
  switch (op.op) {
    case 'equip': {
      const idx = p.backpack.findIndex((it) => it.uid === op.uid);
      if (idx < 0) return no('build.noItem');
      const item = p.backpack[idx];
      const def = ITEMS[item.def];
      if (!def) return no('build.noItem');
      if (!canEquipItem(p.heroId, item.def)) return no('build.wrongWeapon');
      const old = p.equipment[def.slot];
      if (def.slot === 'weapon') p.equipment.weapon = item;
      else p.equipment[def.slot] = item;
      if (old) p.backpack[idx] = old;
      else p.backpack.splice(idx, 1);
      r = ok;
      break;
    }
    case 'unequip': {
      if (p.backpack.length >= BACKPACK_SLOTS) return no('build.bagFull');
      if (op.slot === 'weapon') {
        // the hero is never weaponless: the piece goes to the bag and their plain weapon comes back
        const base = HEROES[p.heroId].weapon;
        const cur = p.equipment.weapon;
        if (cur.def === base && cur.rarity === 0 && !cur.affixes.length) return no('build.baseWeapon');
        p.backpack.push(cur);
        p.equipment.weapon = { uid: world.allocId(), def: base, rarity: 0, affixes: [] };
      } else {
        const cur = p.equipment[op.slot];
        if (!cur) return no('build.noItem');
        p.backpack.push(cur);
        p.equipment[op.slot] = undefined;
      }
      r = ok;
      break;
    }
    case 'drop': {
      const idx = p.backpack.findIndex((it) => it.uid === op.uid);
      let item: ItemInst | undefined;
      if (idx >= 0) item = p.backpack.splice(idx, 1)[0];
      else if (p.equipment.armor?.uid === op.uid) {
        item = p.equipment.armor;
        p.equipment.armor = undefined;
      } else if (p.equipment.charm?.uid === op.uid) {
        item = p.equipment.charm;
        p.equipment.charm = undefined;
      }
      if (!item) return no('build.noItem');
      spawnPickup(world, p.x + world.rng.float(-30, 30), p.y + world.rng.float(-30, 30), { type: 'item', item, owner: 0, ttl: 120 });
      r = ok;
      break;
    }
    default:
      r = applyKitOp(p, op, playerSlot);
  }
  if (!r.ok) return r;
  recomputeStats(world, p);
  p.hp = Math.min(p.hp, p.maxHp);
  return r;
}

// ------------------------------------------------------------------ a hero resting in a slot

export function saveSpent(save: HeroSave): number {
  return kitSpent(save);
}

export function saveFree(save: HeroSave): number {
  return kitFree(save);
}

export function enforceSaveBudget(save: HeroSave): void {
  enforceKit(save, saveSlot);
}

/** The same changes on a hero waiting between realms; gear moves through the saved backpack. */
export function applySaveBuild(save: HeroSave, op: SaveOp): BuildResult {
  switch (op.op) {
    case 'equip': {
      const item = save.backpack[op.index];
      if (!item) return no('build.noItem');
      const def = ITEMS[item.def];
      if (!def) return no('build.noItem');
      if (!canEquipItem(save.heroId, item.def)) return no('build.wrongWeapon');
      const old: ItemSave | undefined = save.equipment[def.slot];
      if (def.slot === 'weapon') save.equipment.weapon = item;
      else save.equipment[def.slot] = item;
      if (old) save.backpack[op.index] = old;
      else save.backpack.splice(op.index, 1);
      return ok;
    }
    case 'unequip': {
      if (save.backpack.length >= BACKPACK_SLOTS) return no('build.bagFull');
      if (op.slot === 'weapon') {
        const base = HEROES[save.heroId].weapon;
        const cur = save.equipment.weapon;
        if (cur.def === base && cur.rarity === 0 && !(cur.affixes ?? []).length) return no('build.baseWeapon');
        save.backpack.push(cur);
        save.equipment.weapon = { def: base, rarity: 0, affixes: [] };
      } else {
        const cur = save.equipment[op.slot];
        if (!cur) return no('build.noItem');
        save.backpack.push(cur);
        save.equipment[op.slot] = undefined;
      }
      return ok;
    }
    default:
      return applyKitOp(save, op, saveSlot);
  }
}

export function backpackFull(p: PlayerEnt): boolean {
  return p.backpack.length >= BACKPACK_SLOTS;
}
